import React, { useCallback, useEffect, useState } from "react";
import { apiClient, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";
import { hasPerm } from "../../lib/permissions";
import { ImportJobsSection } from "./ImportJobsPanel";
import { Loader2, FileText, CheckCircle2, XCircle, Ban, X, ExternalLink, ShieldCheck, Trash2 } from "lucide-react";

const ST = {
  pending_review: { c: "#c9a55c", ar: "بانتظار المراجعة", en: "Pending review" },
  approved: { c: "#16a34a", ar: "معتمد", en: "Approved" },
  rejected: { c: "#ef4444", ar: "مرفوض", en: "Rejected" },
  revoked: { c: "#6b6b6b", ar: "ملغى", en: "Revoked" },
  expired: { c: "#8b5cf6", ar: "منتهي الصلاحية", en: "Expired" },
};
const ACT = { grant: ["منح المعلن التفويض", "Granted by advertiser"], advertiser_revoke: ["ألغاه المعلن", "Revoked by advertiser"], create: ["إنشاء", "Created"], document_uploaded: ["رفع وثيقة", "Document uploaded"], approve: ["اعتماد", "Approved"], reject: ["رفض", "Rejected"], revoke: ["إلغاء", "Revoked"] };
const input = "input-dark text-sm w-full";

const Pill = ({ s, ar }) => {
  const x = ST[s] || { c: "#8a8a8a", ar: s, en: s };
  return <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ color: x.c, background: x.c + "1f", border: `1px solid ${x.c}55` }} data-testid={`auth-status-${s}`}>{ar ? x.ar : x.en}</span>;
};

export default function ImportAuthorizationsTab() {
  const { lang } = useI18n();
  const ar = lang === "ar";
  const { user } = useAuth();
  const canApprove = hasPerm(user, "listings.imports.approve");
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await apiClient.get("/admin/imports/authorizations", { params: status ? { status } : {} });
      setItems(data.items || []);
    } catch (e) { setErr(formatApiError(e?.response?.data?.detail ?? e?.message)); } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-imports-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl">{ar ? "تفويضات استيراد الإعلانات" : "Listing import authorizations"} ({items.length})</h2>
          <p className="text-xs text-[#6b6b6b] mt-1">{ar ? "يمنح المعلن التفويض بموافقته الصريحة من لوحة معرضه، ثم يراجعه المشرف ويعتمده ويبدأ الاستيراد نيابةً عنه. لا يُنشر أي إعلان إلا يدويًا بعد المراجعة." : "Advertisers grant authorizations with explicit consent from their dealership dashboard; admins review, approve and run the import on their behalf. Nothing is published without manual review."}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {["", "pending_review", "approved", "rejected", "revoked"].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`imports-filter-${s || "all"}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${status === s ? "bg-[#c9a55c] text-[#0a0a0a]" : "border border-[#0a0a0a]/15"}`}>
            {s ? (ar ? ST[s].ar : ST[s].en) : (ar ? "الكل" : "All")}
          </button>
        ))}
      </div>
      {err && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 mb-3" data-testid="imports-error">{err}</div>}
      <div className="surface rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="imports-table">
          <thead className="bg-[#0a0a0a]/[0.04] text-xs text-[#3a3a3a]">
            <tr>{(ar ? ["المعرض", "المصدر", "النطاق", "ينتهي", "الموافقة", "الحالة", ""] : ["Dealership", "Source", "Scope", "Expires", "Consent", "Status", ""]).map((h, i) => <th key={i} className="p-3 text-start">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="7" className="p-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
              : items.length === 0 ? <tr><td colSpan="7" className="p-8 text-center text-[#6b6b6b]" data-testid="imports-empty">{ar ? "لا توجد تفويضات." : "No authorizations."}</td></tr>
              : items.map((a) => (
                <tr key={a.id} onClick={() => setOpenId(a.id)} className="border-t border-[#0a0a0a]/[0.06] hover:bg-[#fafaf7] cursor-pointer" data-testid={`imports-row-${a.id}`}>
                  <td className="p-3 font-semibold">{a.business_name || a.business_id}</td>
                  <td className="p-3" dir="ltr">{a.source_domain}</td>
                  <td className="p-3 text-xs">{a.scope === "listed_urls" ? (ar ? `${a.listing_urls.length} رابط` : `${a.listing_urls.length} URLs`) : (ar ? "كل إعلانات الحساب" : "All profile listings")} · {ar ? "حد" : "max"} {a.max_listings}</td>
                  <td className="p-3 text-xs" dir="ltr">{(a.expires_at || "").slice(0, 10)}</td>
                  <td className="p-3">{a.consent ? <ShieldCheck className="w-4 h-4 text-[#16a34a]" /> : a.document ? <FileText className="w-4 h-4 text-[#16a34a]" /> : <span className="text-xs text-[#8a8a8a]">—</span>}</td>
                  <td className="p-3"><Pill s={a.effective_status} ar={ar} /></td>
                  <td className="p-3">{canApprove && <button onClick={(e) => { e.stopPropagation(); setDelTarget(a); }} aria-label="delete" title={ar ? "حذف نهائي" : "Delete permanently"}
                    className="inline-flex items-center gap-1 rounded-full border border-red-300 px-2.5 py-1 text-[11px] font-bold text-red-700 hover:bg-red-50" data-testid={`imports-delete-${a.id}`}><Trash2 className="w-3 h-3" />{ar ? "حذف نهائي" : "Delete"}</button>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {openId && <Detail id={openId} ar={ar} canApprove={canApprove} onClose={() => setOpenId(null)} onChanged={load} />}
      {delTarget && <DeleteDialog a={delTarget} ar={ar} onClose={() => setDelTarget(null)} onDeleted={() => { setDelTarget(null); load(); }} />}
    </div>
  );
}

function Detail({ id, ar, canApprove, onClose, onChanged }) {
  const [a, setA] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => apiClient.get(`/admin/imports/authorizations/${id}`).then(({ data }) => setA(data)).catch((e) => setErr(formatApiError(e?.response?.data?.detail ?? e?.message))), [id]);
  useEffect(() => { load(); }, [load]);
  const run = async (key, fn) => {
    setBusy(key); setErr(null);
    try { const { data } = await fn(); setA(data); setNote(""); onChanged(); } catch (e) { setErr(formatApiError(e?.response?.data?.detail ?? e?.message)); } finally { setBusy(null); }
  };
  const decide = (action) => run(action, () => apiClient.post(`/admin/imports/authorizations/${id}/${action}`, { note: note.trim() || null }));
  const viewDoc = async () => {
    try {
      const res = await apiClient.get(`/admin/imports/authorizations/${id}/document`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch (e) { setErr(formatApiError(e?.response?.data?.detail ?? e?.message)); }
  };
  if (!a) return null;
  const s = a.effective_status;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="imports-detail">
      <div className="w-full max-w-xl h-full overflow-y-auto bg-white p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div><div className="font-display text-xl">{a.business_name}</div><div className="text-xs text-[#6b6b6b]" dir="ltr">{a.source_domain} · {a.id}</div></div>
          <div className="flex items-center gap-3"><Pill s={s} ar={ar} /><button onClick={onClose} aria-label="close" data-testid="imports-detail-close"><X className="w-4 h-4" /></button></div>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {[[ar ? "رابط الحساب" : "Profile", <a href={a.source_profile_url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1 break-all" dir="ltr">{a.source_profile_url}<ExternalLink className="w-3 h-3" /></a>],
            [ar ? "النطاق" : "Scope", a.scope === "listed_urls" ? `${a.listing_urls.length} URLs` : (ar ? "كل إعلانات الحساب" : "All profile listings")],
            [ar ? "المحتوى المسموح" : "Allowed content", a.allowed_content.join(", ")], [ar ? "الحد الأقصى" : "Max listings", a.max_listings],
            [ar ? "الموقّع" : "Signatory", `${a.signatory_name} — ${a.signatory_role}`],
            ...((a.decisions || []).some((d) => d.action === "approve") ? [[ar ? "اعتمده" : "Approved by", <span data-testid="imports-approved-by">{a.approved_by_name || (ar ? "غير متوفر" : "Not available")}</span>]] : []), [ar ? "التوقيع / الانتهاء" : "Signed / expires", `${a.signed_at.slice(0, 10)} → ${a.expires_at.slice(0, 10)}`]]
            .map(([k, v]) => <React.Fragment key={k}><dt className="text-[#6b6b6b]">{k}</dt><dd>{v}</dd></React.Fragment>)}
        </dl>
        {a.scope === "listed_urls" && <ul className="text-xs font-mono space-y-0.5 max-h-32 overflow-y-auto" dir="ltr">{a.listing_urls.map((u) => <li key={u} className="break-all">{u}</li>)}</ul>}
        {a.notes && <p className="text-sm text-[#525252]">{a.notes}</p>}

        {a.consent ? <ConsentBlock c={a.consent} ar={ar} /> : a.document && (
          <section className="rounded-xl border border-[#0a0a0a]/10 p-4 text-xs space-y-1" data-testid="imports-document">
            <div className="text-sm font-bold">{ar ? "وثيقة تفويض سابقة" : "Legacy signed document"}</div>
            <div>{a.document.filename} · <span dir="ltr">SHA-256 {a.document.sha256.slice(0, 16)}…</span></div>
            <button onClick={viewDoc} className="inline-flex items-center gap-1 font-bold underline" data-testid="imports-doc-view"><FileText className="w-3 h-3" />{ar ? "عرض الوثيقة" : "View document"}</button>
          </section>
        )}

        {a.status === "approved" && <ImportJobsSection auth={a} ar={ar} canApprove={canApprove} />}

        {canApprove && ["pending_review", "approved"].includes(a.status) && s !== "expired" && (
          <section className="rounded-xl border border-[#0a0a0a]/10 p-4 space-y-3" data-testid="imports-decision">
            <div className="text-sm font-bold">{ar ? "القرار" : "Decision"}</div>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={input} placeholder={ar ? "ملاحظة (إلزامية للرفض والإلغاء)" : "Note (required to reject / revoke)"} data-testid="imports-decision-note" />
            <div className="flex flex-wrap gap-2">
              {a.status === "pending_review" && <>
                <DecisionBtn icon={CheckCircle2} busy={busy === "approve"} onClick={() => decide("approve")} cls="bg-[#16a34a] text-white" t={ar ? "اعتماد" : "Approve"} id="approve" />
                <DecisionBtn icon={XCircle} busy={busy === "reject"} onClick={() => decide("reject")} cls="bg-red-600 text-white" t={ar ? "رفض" : "Reject"} id="reject" />
              </>}
              {a.status === "approved" && <DecisionBtn icon={Ban} busy={busy === "revoke"} onClick={() => decide("revoke")} cls="border border-red-400 text-red-700" t={ar ? "إلغاء التفويض" : "Revoke"} id="revoke" />}
            </div>
          </section>
        )}
        {!canApprove && a.status === "pending_review" && <div className="text-xs text-[#8a8a8a]" data-testid="imports-no-approve-perm">{ar ? "الاعتماد يتطلب صلاحية «اعتماد تفويضات الاستيراد»." : "Approval requires the 'Approve import authorizations' permission."}</div>}
        {err && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="imports-detail-error">{err}</div>}

        <section data-testid="imports-decisions">
          <div className="text-sm font-bold mb-2">{ar ? "سجل القرارات" : "Decision trail"}</div>
          <ol className="space-y-2 text-xs">
            {[...(a.decisions || [])].reverse().map((d, i) => (
              <li key={i} className="border-s-2 border-[#c9a55c] ps-3">
                <div className="font-semibold">{ACT[d.action] ? ACT[d.action][ar ? 0 : 1] : d.action} {d.to && d.from !== d.to ? `→ ${ar ? ST[d.to]?.ar : ST[d.to]?.en}` : ""}</div>
                <div className="text-[#6b6b6b]" dir="ltr">{d.at.slice(0, 16).replace("T", " ")} · {d.by_name ? `${d.by_name} — ` : ""}{d.by_email}</div>
                {d.note && d.action !== "document_uploaded" && <div className="text-[#3a3a3a]">{d.note}</div>}
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}

const ConsentBlock = ({ c, ar }) => (
  <section className="rounded-xl border border-[#16a34a]/30 bg-[#16a34a]/[0.04] p-4 text-xs space-y-2" data-testid="imports-consent">
    <div className="text-sm font-bold inline-flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-[#16a34a]" />{ar ? "موافقة المعلن الإلكترونية" : "Advertiser e-consent"}</div>
    <div>{c.user_name || c.user_email} · {c.role_key === "branch_manager" ? (ar ? "مدير" : "Manager") : (ar ? "المالك" : "Owner")} · <span dir="ltr">{c.accepted_at.slice(0, 16).replace("T", " ")} · IP {c.ip || "—"} · v{c.version}</span></div>
    <details><summary className="cursor-pointer font-semibold">{ar ? "نص التفويض الموافَق عليه" : "Accepted authorization text"}</summary><pre className="whitespace-pre-wrap font-sans mt-2 text-[#3a3a3a]">{c.text}</pre><div className="font-mono text-[#8a8a8a] break-all" dir="ltr">SHA-256 {c.text_sha256}</div></details>
  </section>
);

const DecisionBtn = ({ icon: Icon, busy, onClick, cls, t, id }) => (
  <button onClick={onClick} disabled={busy} className={`inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-bold disabled:opacity-50 ${cls}`} data-testid={`imports-${id}-btn`}>
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}{t}
  </button>
);

const BLOCK = {
  active_listings: (n, ar) => (ar ? `مرتبط به ${n} إعلان منشور أو نشط حاليًا` : `${n} published/active listings are linked to it`),
  running_jobs: (n, ar) => (ar ? `لديه ${n} عملية استيراد جارية — ستُوقف عند الحذف` : `${n} import runs in progress — they will be stopped`),
};

function DeleteDialog({ a, ar, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [blockers, setBlockers] = useState(null);
  const del = async (force) => {
    setBusy(true); setErr(null);
    try { await apiClient.delete(`/admin/imports/authorizations/${a.id}`, { params: force ? { force: true } : {} }); onDeleted(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "delete_blocked") setBlockers(d.blockers);
      else setErr(formatApiError(d ?? e?.message));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="imports-delete-dialog">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="font-display text-lg inline-flex items-center gap-2 text-red-700"><Trash2 className="w-5 h-5" />{ar ? "حذف طلب الاستيراد نهائيًا" : "Delete import request permanently"}</div>
        <p className="text-sm text-[#3a3a3a]">{ar
          ? `سيُحذف الطلب ${a.request_number || ""} (${a.business_name || ""} · ${a.source_domain}) من القائمة وقاعدة البيانات، ولا يمكن التراجع. تبقى عمليات الاستيراد والإعلانات المرتبطة كما هي، ويُحفظ سجل التدقيق.`
          : `Request ${a.request_number || ""} (${a.business_name || ""} · ${a.source_domain}) will be removed from the list and the database. This cannot be undone. Linked import runs and listings are kept, and the audit log is preserved.`}</p>
        {blockers && <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm space-y-1" data-testid="imports-delete-blockers">
          <div className="font-bold text-amber-900">{ar ? "تعذّر الحذف للأسباب التالية:" : "Deletion was blocked because:"}</div>
          {blockers.map((b) => <div key={b.code} className="text-amber-900" data-testid={`imports-delete-blocker-${b.code}`}>• {(BLOCK[b.code] || ((n) => `${b.code} (${n})`))(b.count, ar)}</div>)}
          <div className="text-[12px] text-[#525252]">{ar ? "يمكنك الحذف على كل حال: يُحذف الطلب فقط، وتبقى الإعلانات المنشورة كما هي." : "You can delete anyway: only the request is removed; published listings stay as they are."}</div>
        </div>}
        {err && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="imports-delete-error">{err}</div>}
        <div className="flex flex-wrap justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="rounded-full border border-[#0a0a0a]/15 px-4 py-2 text-sm font-semibold" data-testid="imports-delete-cancel">{ar ? "إلغاء" : "Cancel"}</button>
          <button onClick={() => del(!!blockers)} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-red-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50" data-testid={blockers ? "imports-delete-force" : "imports-delete-confirm"}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}{blockers ? (ar ? "حذف على كل حال" : "Delete anyway") : (ar ? "حذف نهائي" : "Delete permanently")}
          </button>
        </div>
      </div>
    </div>
  );
}
